import type { EngineParticipant, EngineResult, GeneratedMatch } from './types';
import { generateSingleElim } from './single-elim';
import { nextPowerOfTwo } from './seed-order';

function lowerId(round: number, slot: number): string {
  return `DE-L-R${round}-S${slot}`;
}
function grandFinalId(): string {
  return 'DE-GF';
}

/**
 * Double Elimination.
 *
 * Для bracketSize N = 2^r:
 * - Upper: r раундов, N-1 матчей (включая upper-final).
 * - Lower: 2*(r-1) раундов, N-2 матчей.
 * - Grand Final: 1 матч.
 * Итого 2N-2.
 *
 * Структура нижней сетки:
 * - Нечётные раунды (L1, L3, ...) — победители предыдущего раунда играют между собой
 *   (для L1 — проигравшие из U1 играют парами).
 * - Чётные раунды (L2, L4, ...) — победители предыдущего нижнего раунда играют
 *   с проигравшими из верхней сетки (slot A — снизу, slot B — сверху).
 *
 * Число матчей в раунде L_rL: N / 2^(ceil(rL/2)+1).
 *   L1=L2=N/4, L3=L4=N/8, ...
 *
 * Куда приходят проигравшие из upper:
 * - U1 (N/2 проигравших) → L1 парами.
 * - U_k, k>=2 (N/2^k проигравших) → L_{2(k-1)} в slot B.
 * - Upper-final проигравший → lower-final в slot B.
 */
export function generateDoubleElim(participants: EngineParticipant[]): EngineResult {
  if (participants.length < 2) {
    throw new Error('Нужно минимум 2 участника');
  }
  const bracketSize = nextPowerOfTwo(participants.length);
  const r = Math.log2(bracketSize);

  // Upper — переиспользуем SE-функцию, перешиваем id с префиксом "DE-U-".
  const upper = generateSingleElim(participants);
  const upperMatches: GeneratedMatch[] = upper.matches.map((m) => ({
    ...m,
    id: `DE-U-${m.id.replace(/^SE-/, '')}`,
  }));
  for (const m of upperMatches) {
    if (m.nextMatchId) {
      m.nextMatchId = `DE-U-${m.nextMatchId.replace(/^SE-/, '')}`;
    }
  }

  const upperFinal = upperMatches.find((m) => m.round === r)!;
  upperFinal.nextMatchId = grandFinalId();
  upperFinal.nextMatchSlot = 'A';

  // Lower bracket: 2*(r-1) раундов.
  const lowerRoundsCount = 2 * (r - 1);
  const lowerMatches: GeneratedMatch[] = [];

  function matchesInLowerRound(rL: number): number {
    const blockExp = Math.ceil(rL / 2);
    return bracketSize / Math.pow(2, blockExp + 1);
  }

  for (let rL = 1; rL <= lowerRoundsCount; rL++) {
    const count = matchesInLowerRound(rL);
    for (let s = 1; s <= count; s++) {
      lowerMatches.push({
        id: lowerId(rL, s),
        stage: 'LOWER',
        round: rL,
        slot: s,
        playerAId: null,
        playerBId: null,
      });
    }
  }

  // Сшиваем nextMatchId внутри нижней сетки.
  for (let rL = 1; rL < lowerRoundsCount; rL++) {
    const cur = lowerMatches
      .filter((m) => m.round === rL)
      .sort((a, b) => a.slot - b.slot);
    const next = lowerMatches
      .filter((m) => m.round === rL + 1)
      .sort((a, b) => a.slot - b.slot);
    const isOdd = rL % 2 === 1;
    if (isOdd) {
      // Нечётный → чётный: число матчей одинаковое. Победитель slot s → slot s, в slot A.
      // (slot B чётного раунда зарезервирован под проигравшего из upper).
      cur.forEach((m, idx) => {
        m.nextMatchId = next[idx].id;
        m.nextMatchSlot = 'A';
      });
    } else {
      // Чётный → нечётный: число матчей вдвое меньше. Победители пар идут A/B.
      cur.forEach((m, idx) => {
        const targetIdx = Math.floor(idx / 2);
        m.nextMatchId = next[targetIdx].id;
        m.nextMatchSlot = idx % 2 === 0 ? 'A' : 'B';
      });
    }
  }

  // Финал нижней → Grand Final в slot B.
  const lowerFinal = lowerMatches[lowerMatches.length - 1];
  lowerFinal.nextMatchId = grandFinalId();
  lowerFinal.nextMatchSlot = 'B';

  // Соединяем upper → lower (проигравшие).
  for (const u of upperMatches) {
    if (u.round === r) {
      // Проигравший upper-финала → финал нижней, slot B.
      u.loserNextMatchId = lowerFinal.id;
      u.loserNextMatchSlot = 'B';
      continue;
    }

    if (u.round === 1) {
      // U1 → L1 парами. U-slot 1,2 → L-slot 1; 3,4 → L-slot 2; ...
      const targetIdx = Math.floor((u.slot - 1) / 2);
      const target = lowerMatches.filter((m) => m.round === 1)[targetIdx];
      u.loserNextMatchId = target.id;
      // Чётные/нечётные слоты upper → slot A/B нижнего матча.
      u.loserNextMatchSlot = (u.slot - 1) % 2 === 0 ? 'A' : 'B';
    } else {
      // U_k (k>1) → L_{2(k-1)} в slot B (slot A зарезервирован под победителя L_{2k-3}).
      const targetRoundL = 2 * (u.round - 1);
      const inThatRound = lowerMatches
        .filter((m) => m.round === targetRoundL)
        .sort((a, b) => a.slot - b.slot);
      const target = inThatRound[u.slot - 1];
      u.loserNextMatchId = target.id;
      u.loserNextMatchSlot = 'B';
    }
  }

  // Grand Final.
  const grandFinal: GeneratedMatch = {
    id: grandFinalId(),
    stage: 'MAIN',
    round: r + 1,
    slot: 1,
    playerAId: null,
    playerBId: null,
  };

  // ──────────────────────────────────────────────────────────────────────
  // Схлопывание byes в нижней сетке.
  //
  // Если число участников не степень двойки, верхняя сетка содержит bye-матчи
  // (один игрок, второй null, autoAdvanceWinnerId задан). Такой матч НЕ даёт
  // проигравшего — соответствующий слот lower-матча навсегда пуст. Без обработки
  // нижний матч никогда не станет играбельным → deadlock.
  //
  // Алгоритм: проходим lower-матчи слева направо по раундам и считаем «живые»
  // источники каждого матча. Источник «живой», если он реально может прислать
  // игрока. Если у lower-матча ровно один живой источник — он сам становится
  // bye-матчем (autoAdvanceWinnerId), и его «победитель» продвигается дальше.
  // Если оба источника мертвы — матч вырожденный (degenerate), его убираем.
  // ──────────────────────────────────────────────────────────────────────
  const degenerate = collapseLowerByes(upperMatches, lowerMatches);

  // Убираем вырожденные lower-матчи (оба источника мертвы — реальных игроков нет).
  const liveLowerMatches = lowerMatches.filter((m) => !degenerate.has(m.id));

  return {
    matches: [...upperMatches, ...liveLowerMatches, grandFinal],
    rounds: r + 1,
  };
}

/**
 * Является ли upper-матч bye-матчем (не даёт проигравшего).
 * Bye помечается единственным признаком — autoAdvanceWinnerId (его проставляет
 * generateSingleElim только для R1-матчей с одним игроком). Матчи старших
 * раундов с null-слотами — это просто несыгранные матчи, они дадут проигравшего.
 */
function isUpperBye(m: GeneratedMatch): boolean {
  return Boolean(m.autoAdvanceWinnerId);
}

/**
 * Схлопывает byes в нижней сетке. Мутирует переданные массивы матчей.
 * Возвращает set id вырожденных lower-матчей, подлежащих удалению.
 */
function collapseLowerByes(
  upperMatches: GeneratedMatch[],
  lowerMatches: GeneratedMatch[]
): Set<string> {
  const degenerate = new Set<string>();

  // 1. Bye-матчи upper не дают проигравшего — снимаем у них loser-связи.
  //    Запоминаем, какие (lowerMatchId, slot) остаются без поставщика.
  const deadSlots = new Set<string>(); // ключ: `${lowerMatchId}:${slot}`
  for (const u of upperMatches) {
    if (isUpperBye(u) && u.loserNextMatchId && u.loserNextMatchSlot) {
      deadSlots.add(`${u.loserNextMatchId}:${u.loserNextMatchSlot}`);
      delete u.loserNextMatchId;
      delete u.loserNextMatchSlot;
    }
  }

  // 2. Проход по lower-раундам слева направо: помечаем вырожденные матчи и
  //    deadSlot'ы. На этапе генерации все lower-слоты ещё пусты (loser-пропагация
  //    происходит в рантайме), поэтому «живость» слота определяется только тем,
  //    помечен ли он deadSlots.
  const maxRound = Math.max(...lowerMatches.map((m) => m.round));
  for (let rL = 1; rL <= maxRound; rL++) {
    const inRound = lowerMatches
      .filter((m) => m.round === rL && !degenerate.has(m.id))
      .sort((a, b) => a.slot - b.slot);

    for (const m of inRound) {
      // Слот мёртв, если помечен deadSlots (его источник — upper-bye либо
      // вырожденный lower-матч). На этапе генерации lower-слоты ещё пусты.
      const aLive = !deadSlots.has(`${m.id}:A`);
      const bLive = !deadSlots.has(`${m.id}:B`);

      if (aLive && bLive) {
        // Нормальный матч — оба источника живые.
        continue;
      }

      if (!aLive && !bLive) {
        // Оба источника мертвы — вырожденный матч. Его слот в следующем
        // матче тоже становится мёртвым.
        degenerate.add(m.id);
        if (m.nextMatchId && m.nextMatchSlot) {
          deadSlots.add(`${m.nextMatchId}:${m.nextMatchSlot}`);
        }
        continue;
      }

      // Ровно один живой источник → матч получит одного реального игрока,
      // второй слот навсегда пуст. Помечаем deadSlot: applyDoubleElimResult
      // при заполнении живого слота схлопнет матч в bye и продвинет игрока.
      m.deadSlot = aLive ? 'B' : 'A';
    }
  }

  return degenerate;
}

/**
 * Помещает игрока в слот матча. Если после этого матч становится bye
 * (deadSlot задан и живой слот заполнен) — каскадно продвигает игрока дальше.
 * Мутирует массив `updated` на месте.
 */
function placePlayer(
  updated: GeneratedMatch[],
  targetId: string,
  slot: 'A' | 'B' | undefined,
  playerId: string
): void {
  const tIdx = updated.findIndex((m) => m.id === targetId);
  if (tIdx < 0) return;
  const target = { ...updated[tIdx] };
  if (slot === 'A') target.playerAId = playerId;
  else if (slot === 'B') target.playerBId = playerId;
  else if (target.playerAId === null) target.playerAId = playerId;
  else if (target.playerBId === null) target.playerBId = playerId;
  updated[tIdx] = target;

  // Каскад: если матч имеет мёртвый слот, а живой только что заполнился —
  // матч схлопывается в bye, его «победитель» продвигается дальше.
  if (
    target.deadSlot &&
    !target.autoAdvanceWinnerId &&
    target.nextMatchId
  ) {
    const liveSlot: 'A' | 'B' = target.deadSlot === 'A' ? 'B' : 'A';
    const livePlayer = liveSlot === 'A' ? target.playerAId : target.playerBId;
    if (livePlayer) {
      target.autoAdvanceWinnerId = livePlayer;
      updated[tIdx] = target;
      placePlayer(updated, target.nextMatchId, target.nextMatchSlot, livePlayer);
    }
  }
}

/**
 * Применяет результат матча: победитель → nextMatchId, проигравший → loserNextMatchId.
 * Слот определяется по nextMatchSlot / loserNextMatchSlot.
 * Bye-матчи нижней сетки (deadSlot) каскадно схлопываются.
 */
export function applyDoubleElimResult(
  matches: GeneratedMatch[],
  matchId: string,
  winnerId: string
): { updatedMatches: GeneratedMatch[] } {
  const idx = matches.findIndex((m) => m.id === matchId);
  if (idx === -1) throw new Error(`Match ${matchId} not found`);
  const match = matches[idx];
  if (winnerId !== match.playerAId && winnerId !== match.playerBId) {
    throw new Error(`Winner ${winnerId} is not a player in ${matchId}`);
  }
  const loserId = winnerId === match.playerAId ? match.playerBId : match.playerAId;

  const updated = matches.slice();

  // Победитель → nextMatchId.
  if (match.nextMatchId) {
    placePlayer(updated, match.nextMatchId, match.nextMatchSlot, winnerId);
  }

  // Проигравший → loserNextMatchId.
  if (match.loserNextMatchId && loserId) {
    placePlayer(updated, match.loserNextMatchId, match.loserNextMatchSlot, loserId);
  }

  return { updatedMatches: updated };
}
